import React from "react";
import { Link } from "react-router-dom";
import { CreditCard, FileText } from "lucide-react";
import { motion } from "framer-motion";
import { useDarkMode } from "../../hooks/useDarkMode";
import { useTranslation } from "../../context/useTranslation";
import { Helmet } from "react-helmet";

const AccountInfoPage = () => {
  const { isDarkMode } = useDarkMode();
  const { t } = useTranslation();

  return (
    <>
      <Helmet>

        {/* ================= PRIMARY SEO ================= */}

        <title>Account Information | Seo Up Rank Dashboard</title>

        <meta
          name="description"
          content="Manage your Seo Up Rank account information including profile settings, billing details, subscription plan and renewal data."
        />

        {/* Private dashboard page */}
        <meta name="robots" content="noindex, nofollow" />

        <link rel="canonical" href="https://novarsis.ai/account-info" />

        {/* ================= OPEN GRAPH ================= */}

        <meta property="og:title" content="Seo Up Rank Account Information" />
        <meta
          property="og:description"
          content="Secure dashboard page to manage your Seo Up Rank account details and subscription settings."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://novarsis.ai/account-info" />

        {/* ================= TWITTER ================= */}

        <meta name="twitter:card" content="summary" />
        <meta
          name="twitter:title"
          content="Seo Up Rank Account Information"
        />
        <meta
          name="twitter:description"
          content="Manage your Seo Up Rank account and billing settings securely."
        />

        {/* ================= SCHEMA ================= */}

        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Seo Up Rank Account Dashboard",
            "url": "https://novarsis.ai/account-info",
            "description": "User dashboard page for managing account information, subscription and billing details."
          }
          `}
        </script>

        {/* ================= BREADCRUMB SCHEMA ================= */}

        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Dashboard",
                "item": "https://novarsis.ai/dashboard"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Account Information",
                "item": "https://novarsis.ai/account-info"
              }
            ]
          }
          `}
        </script>

      </Helmet>

      <div
        className={`min-h-[calc(100vh-120px)] px-4 sm:px-6 lg:px-8 py-8 sm:py-10 ${
          isDarkMode ? "bg-gray-950" : "bg-gray-50"
        }`}
      >
        <div className="max-w-4xl mx-auto">

          {/* ================= HEADER ================= */}

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >

            <h1
              className={`text-4xl sm:text-5xl font-bold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {t("accountInfo")}
            </h1>

            {/* hidden semantic heading */}

            <h2 className="sr-only">
              Manage Seo Up Rank account settings and subscription information
            </h2>

            <p
              className={`mt-3 text-base sm:text-lg ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {t("manageSubscription")}
            </p>

          </motion.div>

          {/* ================= SUBSCRIPTION SECTION ================= */}

          <div className="space-y-8">

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >

              <div className="mb-6">

                <h2
                  className={`text-2xl sm:text-3xl font-bold flex items-center gap-3 ${
                    isDarkMode ? "text-gray-100" : "text-gray-900"
                  }`}
                >
                  <span className="w-1 h-8 bg-[#008300] rounded-full"></span>
                  {t("subscriptionBilling")}
                </h2>

                <p
                  className={`mt-2 text-sm sm:text-base ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  View and manage your current plan, renewal dates, and billing
                  history.
                </p>

              </div>

              {/* ================= CARDS ================= */}

              <div className="space-y-3">

                {[
                  {
                    to: "/dashboard/account-info/current-plan",
                    icon: <CreditCard size={24} />,
                    title: t("currentPlan"),
                    desc: "View your subscription tier (Free / Pro / Enterprise)",
                  },
                  {
                    to: "/dashboard/account-info/renewal-date",
                    icon: <FileText size={24} />,
                    title: t("renewalDateBillingCycle"),
                    desc: "Check your renewal date and billing cycle information",
                  },
                  {
                    to: "/dashboard/account-info/billing-history",
                    icon: <FileText size={24} />,
                    title: t("billingHistory"),
                    desc: "Download invoices and view payment receipts",
                  },
                ].map((item, idx) => (

                  <Link
                    key={idx}
                    to={item.to}
                    className={`group block rounded-xl p-5 sm:p-6 border-2 transition-all duration-300 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700 hover:border-[#008300]"
                        : "bg-white border-gray-200 hover:border-[#008300]"
                    }`}
                  >

                    <div className="flex items-start gap-4">

                      <div
                        className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          isDarkMode
                            ? "bg-gray-700 text-[#008300]"
                            : "bg-green-100 text-[#008300]"
                        }`}
                      >
                        {item.icon}
                      </div>

                      <div className="flex-1">

                        <h3 className="text-base sm:text-lg font-semibold group-hover:text-[#008300]">
                          {item.title}
                        </h3>

                        <p
                          className={`text-sm mt-1 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          {item.desc}
                        </p>

                      </div>

                    </div>

                  </Link>

                ))}

              </div>

            </motion.div>

            {/* ================= FOOTER ================= */}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className={`border-t pt-8 text-center ${
                isDarkMode ? "border-gray-800" : "border-gray-200"
              }`}
            >

              <p className="text-sm text-gray-500">
                {t("allRightsReserved")}
              </p>

            </motion.div>

          </div>

        </div>
      </div>
    </>
  );
};

export default AccountInfoPage;